import Vue from 'vue'
import { ActionTree } from 'vuex'
import { quickSearchByQuery } from '@vue-storefront/core/lib/search'
import SearchQuery from '@vue-storefront/core/lib/search/searchQuery'
import { adjustMultistoreApiUrl } from '@vue-storefront/core/lib/multistore'
import RootState from '@vue-storefront/core/types/RootState'
import ReviewState from '../types/ReviewState'
import * as types from './mutation-types'
import i18n from '@vue-storefront/i18n'
import rootStore from '@vue-storefront/core/store'
import Review from '@vue-storefront/core/modules/review/types/Review'
import { ReviewRequest } from '@vue-storefront/core/modules/review/types/ReviewRequest'
import { Logger } from '@vue-storefront/core/lib/logger'
import config from 'config'
import { processURLAddress } from '@vue-storefront/core/helpers'

/** ms-api's 422 field errors / 429 throttle, as relayed by vuestorefront-api. */
function reviewErrorMessage (status: number, result: any): string {
  if (status === 429 || (result && result.message === 'Too Many Attempts.')) {
    return i18n.t('Too many messages. Please try again later.') as string
  }
  const errors = result && result.errors
  if (errors && errors.customer_email) {
    return i18n.t('Please provide valid e-mail address.') as string
  }
  if (errors) {
    return i18n.t('Some of the form fields are not valid!') as string
  }
  return i18n.t('Something went wrong. Try again in a few seconds.') as string
}

const actions: ActionTree<ReviewState, RootState> = {
  /**
   * Retrieve reviews
   *
   * @param context
   * @param {any} query
   * @param {any} start
   * @param {any} size
   * @param {any} entityType
   * @param {any} sort
   * @param {any} excludeFields
   * @param {any} includeFields
   * @returns {Promise<T> & Promise<any>}
   */
  list (context, {productId, approved = true, start = 0, size = 50, entityType = 'review', sort = '', excludeFields = null, includeFields = null}) {
    let query = new SearchQuery()

    if (productId) {
      query = query.applyFilter({key: 'product_id', value: {'eq': productId}})
    }

    if (approved) {
      query = query.applyFilter({key: 'review_status', value: {'eq': 1}})
    }

    quickSearchByQuery({ query, start, size, entityType, sort, excludeFields, includeFields }).then((resp) => {
      context.commit(types.REVIEW_UPD_REVIEWS, resp)
    }).catch(err => {
      Logger.error(err, 'review')()
    })
  },

  /**
   * Add new review. vuestorefront-api relays ms-api's answer with ms-api's
   * status (422 validation, 429 throttle; a stored review answers 200) in a
   * `{ code, result }` envelope: success is a 2xx with a `result.data` review
   * (K2-3075).
   *
   * @param context
   * @param review
   * @returns {Promise<boolean>} whether the review was stored
   */
  async add (context, review): Promise<boolean> {
    Vue.prototype.$bus.$emit('notification-progress-start', i18n.t('Adding a review ...'))

    let url = config.reviews.create_endpoint

    try {
      const response = await fetch(processURLAddress(url), {
        method: 'POST',
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(review)
      })
      const body = await response.json().catch(() => null)
      const result = body && body.result
      Vue.prototype.$bus.$emit('notification-progress-stop')
      if (response.ok && result && result.data) {
        rootStore.dispatch('notification/spawnNotification', {
          type: 'success',
          message: i18n.t('Thank you, your message has been sent.'),
          action1: { label: i18n.t('OK') }
        })
        Vue.prototype.$bus.$emit('clear-add-review-form')
        return true
      }
      rootStore.dispatch('notification/spawnNotification', {
        type: 'error',
        message: reviewErrorMessage(response.status, result),
        action1: { label: i18n.t('OK') }
      })
      return false
    } catch (e) {
      Vue.prototype.$bus.$emit('notification-progress-stop')
      rootStore.dispatch('notification/spawnNotification', {
        type: 'error',
        message: i18n.t('Something went wrong. Try again in a few seconds.'),
        action1: { label: i18n.t('OK') }
      })
      return false
    }
  }
}

export default actions
